import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ImageUploader from '../../components/admin/ImageUploader';
import FileUploader from '../../components/admin/FileUploader';
import { fetchAttachments, addAttachment, deleteAttachment } from '../../lib/attachmentsApi';
import { fetchEventById, upsertEvent, deleteEvent } from '../../lib/eventsApi';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from '../../lib/cmsUtils';

const emptyForm = {
  title: '',
  description: '',
  date: '',
  location: '',
  external_url: '',
  image_url: '',
  status: 'draft',
};

export default function EventForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState(emptyForm);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const ev = await fetchEventById(id);
      setForm({
        title: ev.title,
        description: ev.description || '',
        date: toDatetimeLocalValue(ev.date),
        location: ev.location || '',
        external_url: ev.external_url || '',
        image_url: ev.image_url || '',
        status: ev.status,
      });
      setAttachments(await fetchAttachments({ eventId: id }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const ensureSaved = async () => {
    if (!isNew) return id;
    const saved = await upsertEvent({
      ...form,
      date: fromDatetimeLocalValue(form.date),
    });
    navigate(`/admin/events/${saved.id}`, { replace: true });
    return saved.id;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        date: fromDatetimeLocalValue(form.date),
      };
      if (!isNew) payload.id = id;
      await upsertEvent(payload);
      navigate('/admin/events');
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this event?')) return;
    await deleteEvent(id);
    navigate('/admin/events');
  };

  const handleAttachmentUpload = async ({ url, name, type }) => {
    const eventId = await ensureSaved();
    const att = await addAttachment({
      event_id: eventId,
      file_url: url,
      file_name: name,
      file_type: type,
    });
    setAttachments((prev) => [...prev, att]);
  };

  if (loading) return <p>Loading…</p>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-brand-dark mb-8">{isNew ? 'New event' : 'Edit event'}</h1>

      <form onSubmit={handleSave} className="space-y-6 bg-white p-6 rounded-xl border border-gray-200">
        {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>}

        <div>
          <label className="block text-sm font-medium mb-1">Title *</label>
          <input required value={form.title} onChange={(e) => set('title', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Date *</label>
          <input required type="datetime-local" value={form.date} onChange={(e) => set('date', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={5}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Location</label>
          <input value={form.location} onChange={(e) => set('location', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">External URL</label>
          <input type="url" value={form.external_url} onChange={(e) => set('external_url', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="https://..." />
        </div>

        <ImageUploader
          bucket="event-images"
          pathPrefix="covers"
          currentUrl={form.image_url}
          onUpload={(url) => set('image_url', url)}
          label="Cover image"
        />

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select value={form.status} onChange={(e) => set('status', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>

        <FileUploader
          existingFiles={attachments}
          onUpload={handleAttachmentUpload}
          onDelete={async (attId) => {
            await deleteAttachment(attId);
            setAttachments((prev) => prev.filter((a) => a.id !== attId));
          }}
        />

        <div className="flex gap-3 pt-4">
          <button type="submit" disabled={saving}
            className="bg-brand-purple text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={() => navigate('/admin/events')}
            className="px-6 py-2 border border-gray-300 rounded-lg">Cancel</button>
          {!isNew && (
            <button type="button" onClick={handleDelete}
              className="ml-auto text-red-600 hover:underline">Delete event</button>
          )}
        </div>
      </form>
    </div>
  );
}
